// ═══════════════════════════════════════════════════════════
// Denial Modal — full denial workflow with AI drafting
// ═══════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { IconLoader2, IconCheck, IconAlertTriangle } from '@tabler/icons-react';
import { useAuthStore } from '../../../store/authStore';
import { listExemptionCodes, type ExemptionCode } from '../../../api/jurisdiction';
import { updateRequest } from '../../../api/requests';
import { sendCorrespondence } from '../../../api/correspondence';
import { draftDenialLetter } from '../../../api/ai';
import type { FoiaRequest } from '../../../api/requests';
import type { Requester } from '../../../api/requests';

const JURISDICTION_ID = import.meta.env.VITE_JURISDICTION_ID ?? '1';

interface Props {
  request:    FoiaRequest;
  requester?: Requester | null;
  actorEmail?: string;
  onDenied:  () => void;
  onClose:   () => void;
}

const SUPERVISOR_ROLES = new Set(['admin', 'supervisor', 'legal']);

export default function DenialModal({ request, requester, actorEmail, onDenied, onClose }: Props) {
  const staffRole = useAuthStore(s => s.user?.staffRole);
  const isSupervisor = !!staffRole && SUPERVISOR_ROLES.has(staffRole);

  const [exemptions, setExemptions]         = useState<ExemptionCode[]>([]);
  const [selectedCodes, setSelectedCodes]   = useState<string[]>([]);
  const [withheldDesc, setWithheldDesc]     = useState('');
  const [letterBody, setLetterBody]         = useState('');
  const [sendEmail, setSendEmail]           = useState(true);
  const [aiDrafting, setAiDrafting]         = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const [step, setStep]                     = useState<'select' | 'draft' | 'approve'>('select');

  useEffect(() => {
    listExemptionCodes(JURISDICTION_ID).then(setExemptions);
  }, []);

  function toggleCode(code: string) {
    setSelectedCodes(cs =>
      cs.includes(code) ? cs.filter(c => c !== code) : [...cs, code]
    );
  }

  async function handleAiDraft() {
    setAiDrafting(true);
    try {
      const codes = exemptions.filter(e => selectedCodes.includes(e.code));
      const body = await draftDenialLetter({
        requestNumber:       request.requestNumber,
        requesterName:       requester?.displayName ?? 'Requester',
        subject:             request.subject,
        exemptionCodes:      codes.map(c => ({ code: c.code, title: c.title, citation: c.statutoryCitation })),
        withheldDescription: withheldDesc || 'The requested records',
        jurisdiction:        'the jurisdiction',
      });
      setLetterBody(body);
      setStep('draft');
    } finally {
      setAiDrafting(false);
    }
  }

  async function handleDeny() {
    if (!selectedCodes.length) return;
    setSaving(true);
    try {
      // Update request status to denied
      await updateRequest(request.recordId, {
        status:        'denied',
        closureReason: withheldDesc || selectedCodes.join(', '),
      }, actorEmail);

      // Send denial correspondence if requested
      if (sendEmail && requester?.email && letterBody) {
        await sendCorrespondence({
          requestId:   request.recordId,
          direction:   'outbound',
          toAddresses: [requester.email],
          subject:     `Response to Public Records Request ${request.requestNumber} — Denial`,
          body:        letterBody,
          isAutomated: false,
        }, actorEmail);
      }

      onDenied();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }}>
      <div style={{
        background: 'white', borderRadius: 4, width: 640,
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--color-neutral-100)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>
            Deny Request — {request.requestNumber}
          </h2>
          <div style={{ fontSize: 14, color: 'var(--color-neutral-500)', marginTop: 4 }}>{request.subject}</div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '18px 24px' }}>
          {step === 'select' ? (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)', marginBottom: 4 }}>Select Applicable Exemptions *</div>
                <div style={{ fontSize: 12, color: 'var(--color-neutral-500)', marginBottom: 10 }}>Select all exemptions that apply to this denial.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                  {exemptions.length === 0 && (
                    <div style={{ fontSize: 14, color: 'var(--color-neutral-400)', padding: '16px 0' }}>
                      No exemption codes configured. Add codes in Settings → Exemption Codes.
                    </div>
                  )}
                  {exemptions.map(e => (
                    <label key={e.code} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                      padding: '10px 12px', borderRadius: 4,
                      background: selectedCodes.includes(e.code) ? 'var(--cds-blurple-50)' : 'var(--cds-gray-50)',
                      border: `1px solid ${selectedCodes.includes(e.code) ? '#D4DDFB' : 'var(--color-neutral-200)'}`,
                      transition: 'all 0.1s',
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedCodes.includes(e.code)}
                        onChange={() => toggleCode(e.code)}
                        style={{ marginTop: 2 }}
                      />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-primary-700)' }}>{e.code}</code>
                          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-800)' }}>{e.title}</span>
                        </div>
                        {e.statutoryCitation && <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 2 }}>{e.statutoryCitation}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-field" style={{ marginBottom: 16 }}>
                <label className="form-label">Description of Withheld Records</label>
                <textarea
                  className="form-input" rows={3}
                  style={{ fontFamily: 'inherit', resize: 'vertical' }}
                  placeholder="E.g. Personnel evaluation records for employee John Smith…"
                  value={withheldDesc}
                  onChange={e => setWithheldDesc(e.target.value)}
                />
              </div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral-800)' }}>Denial Letter</span>
                  <button className="btn btn-secondary btn-sm" style={{ gap: 4 }} onClick={() => setStep('select')}>
                    ← Edit Exemptions
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  {selectedCodes.map(code => (
                    <code key={code} style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-primary-700)', background: 'var(--cds-blurple-50)', padding: '2px 7px', borderRadius: 4 }}>{code}</code>
                  ))}
                </div>
              </div>
              <textarea
                className="form-input"
                style={{ width: '100%', minHeight: 280, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.7 }}
                value={letterBody}
                onChange={e => setLetterBody(e.target.value)}
                placeholder="Denial letter will appear here after AI draft or manual entry…"
              />
              {requester?.email && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
                  Send this letter to {requester.email}
                </label>
              )}
            </>
          )}
        </div>

        {/* Approval step */}
        {step === 'approve' && (
          <div style={{ padding: '18px 24px', borderTop: '1px solid var(--color-neutral-100)' }}>
            {isSupervisor ? (
              <div style={{ padding: '14px 16px', background: 'var(--cds-red-50)', border: '1px solid #F7D5D0', borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <IconAlertTriangle size={18} style={{ color: 'var(--cds-red-700)', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--cds-red-900)', marginBottom: 4 }}>Supervisor Approval</div>
                    <div style={{ fontSize: 12, color: 'var(--cds-red-800)', marginBottom: 16 }}>
                      As a supervisor, you can approve this denial directly. This action is permanent and will be logged.
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--cds-red-900)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={approvalConfirmed} onChange={e => setApprovalConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                      I confirm this denial is justified under the cited exemptions and I approve it on behalf of this office.
                    </label>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: '14px 16px', background: 'var(--cds-yellow-50)', border: '1px solid #FED7AA', borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <IconAlertTriangle size={18} style={{ color: 'var(--cds-yellow-500)', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--cds-terracotta-800)', marginBottom: 4 }}>Supervisor Approval Required</div>
                    <div style={{ fontSize: 12, color: 'var(--cds-terracotta-700)', marginBottom: 16 }}>
                      Denials require supervisor or legal approval before they take effect. Confirm that a supervisor ({['supervisor', 'admin', 'legal'].join(', ')}) has reviewed and approved this denial.
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: 'var(--cds-terracotta-800)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={approvalConfirmed} onChange={e => setApprovalConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                      I confirm a supervisor has reviewed and approved this denial.
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--color-neutral-100)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          {step === 'select' ? (
            <>
              <button
                className="btn btn-secondary btn-sm"
                style={{ gap: 4 }}
                disabled={selectedCodes.length === 0 || aiDrafting}
                onClick={handleAiDraft}
              >
                {aiDrafting ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : '✦ AI Draft Letter'}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                disabled={selectedCodes.length === 0}
                onClick={() => setStep('draft')}
              >
                Write Manually →
              </button>
            </>
          ) : step === 'draft' ? (
            <button
              className="btn btn-primary btn-sm"
              style={{ gap: 4, background: 'var(--cds-red-700)', borderColor: 'var(--cds-red-700)' }}
              disabled={selectedCodes.length === 0}
              onClick={() => { setApprovalConfirmed(false); setStep('approve'); }}
            >
              Continue to Approval →
            </button>
          ) : (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setStep('draft')}>← Back</button>
              <button
                className="btn btn-primary btn-sm"
                style={{ gap: 4, background: 'var(--cds-red-700)', borderColor: 'var(--cds-red-700)' }}
                disabled={saving || !approvalConfirmed}
                onClick={handleDeny}
              >
                {saving
                  ? <IconLoader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  : <><IconCheck size={12} /> {isSupervisor ? 'Approve & Deny' : 'Confirm & Deny'}</>
                }
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
